import { useNavigationContainerRef } from 'expo-router';
import { useCallback, useRef } from 'react';
import { useMMKVObject, useMMKVString } from 'react-native-mmkv';

import {
  type StandbyWaitlistJoinFailureReason,
  getStandbyWaitlistJoinFailureReason,
} from '@/api/standby/standbyWaitlistJoinMutation';
import { useToast } from '@/components/_shared/Toaster';
import type { UserInformationFormValues } from '@/components/_shared/UserInformationForm/types';
import type { NotificationConsentChoice } from '@/components/booking/BookingConsentSheet/types';
import { storage, storageKeys } from '@/constants/storage';
import { useUpdateNotificationConsentMutation } from '@/hooks/mutations/booking/useUpdateNotificationConsentMutation';
import { useStandbyWaitlistJoinMutation } from '@/hooks/mutations/standby/useStandbyWaitlistJoinMutation';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import type { Shop } from '@/types/appointment';
import type { Cart, Service } from '@/types/booking';
import {
  classifyCheckoutError,
  getCheckoutErrorContent,
} from '@/utils/checkoutErrors';
import { resetToStandbyWaitlistJoined } from '@/utils/navigationUtils';
import { buildStandbyWaitlistJoinPayload } from '@/utils/standby/buildStandbyWaitlistJoinPayload';
import { setStandbyWaitlistLeaveSession } from '@/utils/standby/standbyWaitlistLeaveTokenStore';
import { buildStandbyWaitlistEventProps } from '@/utils/standby/standbyWaitlistTracking';
import { Tracker } from '@/utils/tracking';

import type { GuestUserInfo, WaitlistSelection } from './types';

interface UseStandbyWaitlistJoinParams {
  shop: Shop;
  cart: Cart | null;
  services?: Service[];
  waitlistSelections: Map<number, WaitlistSelection>;
  shopRoute: string;
  barberId: string;
  cartId: string;
  closeSheets: () => void;
}

const FAILURE_MESSAGE_KEYS = {
  duplicate: 'error.standbyAlreadyOnWaitlist',
  throttled: 'error.throttled',
  unavailable: 'error.standbyWaitlistUnavailable',
} as const satisfies Record<
  Exclude<StandbyWaitlistJoinFailureReason, 'other'>,
  string
>;

export const useStandbyWaitlistJoin = ({
  shop,
  cart,
  services,
  waitlistSelections,
  shopRoute,
  barberId,
  cartId,
  closeSheets,
}: UseStandbyWaitlistJoinParams) => {
  const { t, i18n } = useScreenTranslations('ChooseTimeScreen');
  const { toast } = useToast();
  const navigationRef = useNavigationContainerRef();

  const [existingCustomerId] = useMMKVString(storageKeys.customerId, storage);
  const [, setGuestUserInfo] = useMMKVObject<GuestUserInfo>(
    storageKeys.guestUserInfo,
    storage,
  );
  const wasLoggedInAtStart = useRef(!!existingCustomerId);
  const pendingCustomerRef = useRef<UserInformationFormValues | null>(null);
  const trackingPropsRef = useRef<ReturnType<
    typeof buildStandbyWaitlistEventProps
  > | null>(null);
  // isPending lags a render behind mutate, so a fast double tap would join twice.
  const isInFlightRef = useRef(false);
  const consentRef = useRef<NotificationConsentChoice>({});

  const { mutate: updateNotificationConsent } =
    useUpdateNotificationConsentMutation();

  const recordConsent = useCallback((consent: NotificationConsentChoice) => {
    consentRef.current = { ...consentRef.current, ...consent };
  }, []);

  const clearConsent = useCallback(() => {
    consentRef.current = {};
  }, []);

  const { mutate, isPending } = useStandbyWaitlistJoinMutation({
    onSuccess: ({ entryId, customerId, leaveToken, leaveTokenExpiresAt }) => {
      Tracker.track('waitlist_success', {
        ...trackingPropsRef.current,
        entry_id: entryId,
      });
      if (customerId && Object.keys(consentRef.current).length > 0) {
        updateNotificationConsent({
          shopId: shop.id,
          promotional: consentRef.current,
          customerId,
        });
      }
      setStandbyWaitlistLeaveSession(entryId, {
        leaveToken,
        leaveTokenExpiresAt,
        shopRoute,
        barberId,
        cartId,
      });
      if (!wasLoggedInAtStart.current && pendingCustomerRef.current) {
        setGuestUserInfo(pendingCustomerRef.current);
      }
      closeSheets();
      resetToStandbyWaitlistJoined(navigationRef, {
        entryId,
        shopRoute: shop.route ?? shopRoute,
      });
    },
    onError: (error) => {
      const reason = getStandbyWaitlistJoinFailureReason(error);
      Tracker.track('waitlist_failure', {
        ...trackingPropsRef.current,
        reason,
      });
      toast({
        variant: 'error',
        message:
          reason === 'other'
            ? getCheckoutErrorContent(
                error.checkoutErrorType ?? classifyCheckoutError(error),
                t,
              ).description
            : t(FAILURE_MESSAGE_KEYS[reason]),
      });
    },
    onSettled: () => {
      isInFlightRef.current = false;
      consentRef.current = {};
    },
  });

  const join = useCallback(
    (customer: UserInformationFormValues) => {
      if (isInFlightRef.current) return;

      const [selected] = waitlistSelections;
      if (!selected) return;
      const [appointmentIndex, selection] = selected;
      const cartItem = cart?.items[appointmentIndex];
      if (!cartItem) return;

      isInFlightRef.current = true;
      pendingCustomerRef.current = customer;
      const payload = buildStandbyWaitlistJoinPayload({
        shopId: shop.id,
        customer,
        cartItem,
        selection,
        services,
        language: i18n.language,
      });
      const trackingProps = buildStandbyWaitlistEventProps({
        shopId: shop.id,
        timePreferences: selection.timePreferences,
        anyProfessional: !payload.requestedBarberId,
      });
      trackingPropsRef.current = trackingProps;
      Tracker.track('confirm_waitlist', trackingProps);
      mutate(payload);
    },
    [waitlistSelections, cart?.items, mutate, shop.id, services, i18n.language],
  );

  return { join, isJoining: isPending, recordConsent, clearConsent };
};
